import {
  SBState,
  SBSelectRaw,
  getIdOrModelId,
  baseReducers,
} from '../utils/helpers/ReducerHelper'
import {
  SBAPICreate,
  SBAPIFetchDispatch,
  SBAPIFetchPaginatedDispatch,
  SBAPIDelete,
  SBAPIUpdate,
} from '../utils/helpers/SBAPIHelper'
import { FileType } from '../models/File'
import { FILES_URL } from '../utils/urls'
import { AppDispatch, RootState } from './store'
import { TableParams } from '../models/TableParams'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { fileTypeSchema } from '../models/schema'
import { initialQuery } from '../utils/helpers/crud/models'

export const initialFileTypeQuery: TableParams = {
  ...initialQuery,
}

const initialState: SBState<FileType> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialFileTypeQuery,
}

const slice = createSlice({
  name: 'fileType',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getFileTypesSuccess,
  setQuery: setFileTypeQuery,
  reset: resetFileTypeState,
  resetQueryAndIds: resetFileTypeQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<{ [key: string]: FileType }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectFileTypes = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedFileType = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectFileTypeById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectFileTypesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedFileType =
  (fileType: FileType | number | undefined) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        fileType ? getIdOrModelId<FileType>(fileType) : undefined
      )
    )

export const getFileTypes = (params: TableParams) =>
  SBAPIFetchPaginatedDispatch<FileType>(
    FILES_URL,
    params,
    [fileTypeSchema],
    slice.actions
  )

export const getFileTypeWithId = (id: number) =>
  SBAPIFetchDispatch<FileType>(
    `${FILES_URL}/${id}`,
    fileTypeSchema,
    slice.actions
  )

export const createFileType = (fileType: FileType) =>
  SBAPICreate<FileType>(fileType, FILES_URL, fileTypeSchema, slice.actions)

export const updateFileType = (fileType: FileType) =>
  SBAPIUpdate<FileType>(fileType, `${FILES_URL}/${fileType.id}`, slice.actions)

export const deleteFileType = (fileType: FileType) =>
  SBAPIDelete<FileType>(fileType, `${FILES_URL}/${fileType.id}`, slice.actions)
